/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { ConnectedService } from "@tivocorp/data-domain-lib";
import type { TConnectedSecureParams } from "@tivocorp/data-domain-lib";

import type { IConnectedInteractor } from "../interfaces";
import { Log } from "../logging/Log";
import { DeviceModel } from "../device";
import { ApplicationFeatureArea } from "../enums";
import { TIVO_SERVICES } from "../../config";
import { BaseInteractor } from "./base";
import type { TNetworkOptions } from "../types/TNetworkOptions";

/**
 * Authenticated facade for Connected service operations.
 *
 * @see AuthRejectionArbiter — `/secure` is the arbiter for CC API 401/403s.
 */
export class ConnectedInteractor
  extends BaseInteractor
  implements IConnectedInteractor
{
  private static readonly TAG: string = "ConnectedInteractor";

  public async fetchSecureConnection(
    params: TConnectedSecureParams,
    options?: TNetworkOptions
  ): Promise<void> {
    return this.executeGuarded(
      "fetchSecureConnection",
      async (signal: AbortSignal): Promise<void> => {
        Log.d(ConnectedInteractor.TAG, "fetchSecureConnection() called");
        const service: ConnectedService = new ConnectedService(
          await this.buildServicesParams(
            TIVO_SERVICES.ConnectedService,
            ApplicationFeatureArea.ALL
          )
        );
        await service.fetchSecureConnection(params, signal);
      },
      options
    );
  }

  /**
   * `GET /secure` as the session probe for {@link AuthRejectionArbiter}.
   *
   * Skips auth recovery — a rejection here *is* the verdict, and arbitrating
   * it would probe again — and survives navigation, because one probe is
   * shared by every call rejected at the same time, from any screen.
   */
  public async probeSecureSession(): Promise<void> {
    return this.fetchSecureConnection(
      {
        bodyId: await DeviceModel.getTiVoSettings().getBodyId(),
        xAmznRequestId: DeviceModel.getDeviceUuid()
      },
      { skipAuthRecovery: true, bypassNavigationCancellation: true }
    );
  }
}
